import * as Sentry from '@sentry/react';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { useEffect, useState } from 'react';

import {
  ClockType,
  TandaClockinDto,
  TandaClockinLatestBatchResponse,
  TandaClockinSnapshot,
} from '../components/ClockStatus/types';
import { useAxios } from '../state/api/axios';
import { useSocketJSMessageQueue } from './useSockJS';

dayjs.extend(utc);

/** Path of the snapshot endpoint (BE base path is `/api/v1/...`). */
const SNAPSHOT_PATH = '/api/v1/tanda/clockins/latest';
/** Max UUIDs per snapshot call (server-side cap). */
const BATCH_SIZE = 200;
/** How often to re-validate map entries against today's date. */
const ROLLOVER_CHECK_MS = 60_000;

/** Type guard for the batch envelope — accepts `{items,total}` directly
 *  or wrapped in `{data:{items,total}}`. */
const isClockinBatchResponse = (
  body: unknown
): body is TandaClockinLatestBatchResponse => {
  if (!body || typeof body !== 'object') return false;
  const candidates: unknown[] = [body];
  if ('data' in body && (body as { data: unknown }).data) {
    candidates.push((body as { data: unknown }).data);
  }
  return candidates.some(
    (c) =>
      typeof c === 'object' &&
      c !== null &&
      Array.isArray((c as { items?: unknown }).items)
  );
};

const chunks = <T>(arr: T[], size: number): T[][] => {
  const result: T[][] = [];
  for (let i = 0; i < arr.length; i += size) {
    result.push(arr.slice(i, i + size));
  }
  return result;
};

/**
 * Parses BE `clockin_at` (ISO-8601 datetime with fixed `+00:00` offset)
 * to an epoch millisecond number for safe chronological comparison.
 * Returns `null` for missing/invalid timestamps so callers can treat them
 * as "no timestamp known".
 */
const parseEventTime = (iso: string | null): number | null => {
  if (!iso) return null;
  const t = Date.parse(iso);
  return Number.isFinite(t) ? t : null;
};

/**
 * STOMP-side replacement policy: an incoming event replaces the existing
 * entry only when its `clockin_at` is STRICTLY later. Strict (not `>=`)
 * preserves the entry on a duplicate replay and ensures a stale `start`
 * frame can't clobber a fresher `finish` from a re-snapshot. Null-timestamp
 * frames (e.g. breaks-without-clock) never overwrite a populated entry.
 */
const shouldApplyNewerEvent = (
  existing: TandaClockinDto | undefined,
  incoming: TandaClockinDto
): boolean => {
  if (!existing) return true;
  const incomingTime = parseEventTime(incoming.clockin_at);
  if (incomingTime === null) return false;
  const existingTime = parseEventTime(existing.clockin_at);
  return existingTime === null || incomingTime > existingTime;
};

/** Type guard: a STOMP frame is a valid TandaClockinDto.
 *  Validates every required field so consumers can rely on the typed shape.
 *  `date` is constrained to `YYYY-MM-DD` so callers can compare it directly
 *  against `dayjs().format('YYYY-MM-DD')`. `userId` is regex-checked (not
 *  just non-empty) so obvious junk like `"undefined"` or `"null"` from a
 *  serialization bug is rejected. `tandaUserId` is not validated — it's
 *  unused on FE and will be dropped from BE; once BE drops the field, the
 *  guard must keep accepting the payload. */
const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const isTandaClockinDto = (frame: unknown): frame is TandaClockinDto => {
  if (!frame || typeof frame !== 'object') return false;
  const f = frame as Partial<TandaClockinDto>;
  if (typeof f.userId !== 'string' || !UUID_RE.test(f.userId)) return false;
  if (typeof f.date !== 'string' || !DATE_RE.test(f.date)) return false;
  if (f.type !== 'start' && f.type !== 'finish') return false;
  if (f.clockin_at !== null && typeof f.clockin_at !== 'string') {
    return false;
  }
  return true;
};

/**
 * Fetches the latest Tanda clock-in event of the day for every employee on
 * the kanban, then keeps the result live via STOMP.
 *
 * - Snapshot phase: when `employeeIds` (or `companyId`) changes, calls
 *   `GET /api/v1/tanda/clockins/latest` in batches of 200. On first
 *   successful response, sets `snapshotLoaded = true` so consumers can
 *   distinguish "BE has loaded and reported no entry" from "BE hasn't
 *   responded yet".
 * - Live phase: subscribes to `/topic/tanda/clockins` via the queueing
 *   variant so multiple frames in one tick are not coalesced.
 * - Midnight rollover: a `setInterval` drops map entries whose `date` no
 *   longer matches today, so a long-lived tab doesn't show yesterday's
 *   status across midnight.
 *
 * Note on HTTP client: this hook intentionally uses `useAxios()` directly
 * instead of the project's `useAPI` hook. The snapshot is a fan-out over N
 * batches of 200 employees each, with sequential awaits and per-batch
 * cancellation on unmount — neither pattern maps cleanly to `useAPI`'s
 * single-key per-request Redux state. The raw call is scoped to this hook;
 * all other call sites in the codebase use `useAPI`.
 *
 * @returns Tuple of:
 *   - `latestByEmployee` — fresh `Map<userId, TandaClockinDto>` on every update
 *   - `snapshotLoaded` — `true` after the first successful snapshot response
 *     (even one with zero items). Consumers use this to avoid phantom
 *     warnings during the initial load window.
 */
export const useTandaClockin = (
  companyId: string | undefined,
  employeeIds: string[]
): TandaClockinSnapshot => {
  const axiosRef = useAxios();
  const [latestByEmployee, setLatestByEmployee] = useState<
    Map<string, TandaClockinDto>
  >(() => new Map());
  const [snapshotLoaded, setSnapshotLoaded] = useState(false);
  // `true` after a snapshot finishes with at least one failed or malformed
  // batch. Consumers should surface this (e.g. a non-blocking toast) so users
  // know some employees' badges may be stale until the next poll re-tries.
  const [snapshotDegraded, setSnapshotDegraded] = useState(false);

  // Queueing STOMP subscription — accumulates every frame so bursts at
  // shift start/end don't lose events. `flushStomp()` returns and clears
  // every buffered frame; the queueing hook bumps an internal counter on
  // each new frame to wake the consumer's draining effect.
  const [, flushStomp] = useSocketJSMessageQueue('tanda/clockins');

  // Only show rollups for today. BE returns the rollup date in UTC, but a
  // user in UTC+7 viewing the page after local midnight but before UTC
  // midnight would see `dayjs.utc()` lag behind BE's `date` and the filter
  // would silently drop everything. Match against EITHER UTC today or local
  // today — covers the timezone transition window without showing genuinely
  // stale rollups from previous days.
  const isToday = (date: string): boolean => {
    const todayUtc = dayjs.utc().format('YYYY-MM-DD');
    const todayLocal = dayjs().format('YYYY-MM-DD');
    return date === todayUtc || date === todayLocal;
  };

  // Re-snapshot whenever the employee set (or company) changes.
  useEffect(() => {
    if (!companyId || employeeIds.length === 0) {
      setLatestByEmployee(new Map());
      setSnapshotLoaded(false);
      setSnapshotDegraded(false);
      return;
    }

    const uniqueIds = Array.from(new Set(employeeIds));
    let cancelled = false;

    (async () => {
      // Clear stale `snapshotDegraded` from a previous snapshot so consumers
      // don't briefly see the old flag during this fetch (set at the end).
      setSnapshotDegraded(false);
      const next = new Map<string, TandaClockinDto>();
      let failedBatches = 0;
      for (const batch of chunks(uniqueIds, BATCH_SIZE)) {
        if (cancelled) return;
        try {
          // Use today's local date (matches the same window `isToday`
          // accepts), but tolerate UTC by also accepting today's UTC date
          // in the response filter.
          const todayLocal = dayjs().format('YYYY-MM-DD');
          const response = await axiosRef.current?.get<unknown>(
            import.meta.env.VITE_API_URL + SNAPSHOT_PATH,
            {
              params: { userids: batch.join(','), date: todayLocal },
            }
          );
          const body = response?.data;
          if (body === null || body === undefined) {
            // No response at all — axiosRef was probably null (teardown).
            failedBatches += 1;
            Sentry.captureMessage('[tanda] snapshot batch returned no body', {
              level: 'warning',
              tags: {
                source: 'useTandaClockin',
                phase: 'snapshot-no-response',
              },
              extra: { batchSize: batch.length, companyId },
            });
            continue;
          }
          if (!isClockinBatchResponse(body)) {
            // HTTP 200 but unexpected shape — BE schema change or proxy
            // unwrapping. Surface so this doesn't go unnoticed.
            failedBatches += 1;
            Sentry.captureMessage('[tanda] snapshot batch had malformed body', {
              level: 'warning',
              tags: { source: 'useTandaClockin', phase: 'snapshot-shape' },
              extra: {
                batchSize: batch.length,
                companyId,
                bodyKeys:
                  typeof body === 'object' && body !== null
                    ? Object.keys(body).slice(0, 10)
                    : typeof body,
              },
            });
            continue;
          }
          let malformedItems = 0;
          for (const item of body.items) {
            if (!isTandaClockinDto(item)) {
              malformedItems += 1;
              continue;
            }
            if (!isToday(item.date)) continue;
            next.set(item.userId, item);
          }
          if (malformedItems > 0) {
            // Mirror the STOMP drain effect's Sentry capture so the snapshot
            // path is symmetric — both surface "BE sent us junk" instead of
            // silently dropping it. Also count toward `snapshotDegraded` so
            // consumers' "some employees' badges may be stale" warning
            // fires for BE-schema-drift, not only transport failures.
            failedBatches += 1;
            Sentry.captureMessage(
              `[tanda] dropped ${malformedItems} malformed snapshot items`,
              {
                level: 'warning',
                tags: { source: 'useTandaClockin', phase: 'snapshot-shape' },
                extra: { batchSize: batch.length, companyId },
              }
            );
          }
        } catch (e) {
          failedBatches += 1;
          // Surface to Sentry (console.warn alone is invisible in prod) but
          // do not crash the UI — a failed batch just means "no event for
          // these users today".
          Sentry.captureException(e, {
            tags: { source: 'useTandaClockin', phase: 'snapshot' },
            extra: { batchSize: batch.length, companyId },
          });

          console.warn(
            '[tanda] snapshot batch failed (size=' + batch.length + '):',
            e
          );
        }
      }
      if (cancelled) return;
      // Merge with current state. BE's `type` field is the authoritative
      // "current state" (`start` = clocked in, `finish` = clocked out) and
      // does NOT necessarily correlate with `clockin_at` — BE returns the
      // timestamp of the FIRST clock-in event of the day, which can be
      // earlier than a previous `finish` when a user clocks back in after
      // a break. So we trust the snapshot's `type` field by default.
      //
      // BUT: STOMP frames are queue-and-drain-once (`flushStomp()` clears
      // the queue on read). A frame that arrived between snapshot request
      // and snapshot response is GONE — the post-merge STOMP drain cannot
      // re-apply it. So if the live entry has a strictly-newer event
      // timestamp AND disagrees on `type`, defer to live.
      //
      // Null snapshot timestamps never overwrite populated live entries
      // (the snapshot was probably malformed — let STOMP keep the last
      // good value rather than wiping it).
      setLatestByEmployee((prev) => {
        const merged = new Map(prev);
        for (const [userId, snapshotEntry] of next) {
          const liveEntry = prev.get(userId);
          const snapTime = parseEventTime(snapshotEntry.clockin_at);
          if (!liveEntry) {
            merged.set(userId, snapshotEntry);
            continue;
          }
          if (snapTime === null) continue; // malformed snapshot — keep live
          const liveTime = parseEventTime(liveEntry.clockin_at);
          if (
            liveTime !== null &&
            snapTime < liveTime &&
            liveEntry.type !== snapshotEntry.type
          ) {
            // Live has a fresher event and disagrees on type — STOMP already
            // drained this state and we can't recover it. Skip overwrite.
            continue;
          }
          merged.set(userId, snapshotEntry);
        }
        return merged;
      });
      setSnapshotLoaded(true);
      setSnapshotDegraded(failedBatches > 0);
    })();

    return () => {
      cancelled = true;
    };
    // employeeIds reference changes every 30s polling refresh — re-snapshot.
    // axiosRef is stable (ref).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, employeeIds]);

  // Drain STOMP queue on every render. The queueing hook bumps an internal
  // counter on each new frame, which re-renders this hook, which re-runs
  // this effect — so every frame is drained exactly once. We deliberately
  // omit deps (not even `[flushStomp]`): flushStomp is a stable callback,
  // so adding it would freeze the effect to mount-only and lose frames.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const drained = flushStomp();
    if (drained.length === 0) return;
    const valid = (drained as unknown[]).filter(isTandaClockinDto);
    const dropped = drained.length - valid.length;
    if (dropped > 0) {
      Sentry.captureMessage(
        `[tanda] dropped ${dropped} malformed STOMP frames`,
        { level: 'warning', tags: { source: 'useTandaClockin' } }
      );
    }
    setLatestByEmployee((prev) => {
      // Track whether anything actually changed so we don't return a new
      // Map reference (and trigger a full Provider re-render cascade) when
      // every drained frame failed the date filter or was dropped upstream.
      // Same pattern used in the midnight-rollover effect below. See
      // `shouldApplyNewerEvent` for the replacement rule (strict `>` and
      // null-timestamp preserve).
      let changed = false;
      const next = new Map(prev);
      for (const event of valid) {
        if (!isToday(event.date)) continue;
        const existing = next.get(event.userId);
        if (shouldApplyNewerEvent(existing, event)) {
          next.set(event.userId, event);
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  });

  // Midnight rollover: every minute, drop entries whose `date` no longer
  // matches today. A long-lived tab would otherwise keep yesterday's
  // clock-in/out pill visible until the next 30s `assignData` poll
  // triggered a re-snapshot (which itself uses today's date).
  useEffect(() => {
    const id = setInterval(() => {
      setLatestByEmployee((prev) => {
        let changed = false;
        const filtered = new Map<string, TandaClockinDto>();
        for (const [userId, entry] of prev) {
          if (isToday(entry.date)) {
            filtered.set(userId, entry);
          } else {
            changed = true;
          }
        }
        return changed ? filtered : prev;
      });
    }, ROLLOVER_CHECK_MS);
    return () => clearInterval(id);
  }, []);

  return { latestByEmployee, snapshotLoaded, snapshotDegraded };
};

// Re-export so consumers can narrow `ClockType` without an extra import.
export type { ClockType };
